/**
 * exportDashboardPdf.js
 *
 * Production-ready, premium programmatic PDF report exporter.
 * Generates an executive board presentation-grade Portrait A4 PDF with:
 *   - Standard A4 Portrait sheet size (210mm x 297mm).
 *   - Custom headers, footers, aspect-ratio dynamic logo, and margins.
 *   - Programmatically drawn high-fidelity vector Cover and compact KPI table.
 *   - High-DPI vertically stacked embedded canvas captures of Recharts analytics charts.
 *   - Beautiful, clean data tables for Resource allocations and Proposal directory.
 *   - All frontend-only, fast, zero package dependencies beyond html2canvas + jsPDF.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import logoImg from '../../../assets/images/exa svg.svg';

// ── Constants ──────────────────────────────────────────────────────────────────
const PDF_ORIENTATION = 'portrait';
const PDF_UNIT = 'mm';
const PDF_FORMAT = 'a4';

// ── SVG Inlining Helper ─────────────────────────────────────────────────────────
function inlineSVGs(container) {
    if (!container) return [];
    const svgs = container.querySelectorAll('svg');
    const originals = [];

    svgs.forEach((svg) => {
        const clone = svg.cloneNode(true);
        const bbox = svg.getBoundingClientRect();
        clone.setAttribute('width', bbox.width);
        clone.setAttribute('height', bbox.height);

        const serialised = new XMLSerializer().serializeToString(clone);
        const encoded = encodeURIComponent(serialised);
        const dataURI = `data:image/svg+xml;charset=utf-8,${encoded}`;

        const img = document.createElement('img');
        img.src = dataURI;
        img.width = bbox.width;
        img.height = bbox.height;
        img.style.display = 'block';

        originals.push({ svg, parent: svg.parentNode, nextSibling: svg.nextSibling });
        svg.parentNode.replaceChild(img, svg);
    });

    return originals;
}

function restoreSVGs(originals) {
    originals.forEach(({ svg, parent, nextSibling }) => {
        const img = parent.querySelector('img[src^="data:image/svg+xml"]');
        if (img) {
            parent.replaceChild(svg, img);
        } else if (nextSibling) {
            parent.insertBefore(svg, nextSibling);
        } else {
            parent.appendChild(svg);
        }
    });
}

// ── Base64 Logo Loader Helper (Preserves Aspect Ratio and Crops Empty Transparent Padding) ──
const trimCanvas = (canvas) => {
    try {
        const ctx = canvas.getContext('2d');
        const width = canvas.width;
        const height = canvas.height;
        const imgData = ctx.getImageData(0, 0, width, height);
        const data = imgData.data;

        let minX = width, minY = height, maxX = 0, maxY = 0;

        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const alpha = data[(y * width + x) * 4 + 3];
                if (alpha > 0) {
                    if (x < minX) minX = x;
                    if (y < minY) minY = y;
                    if (x > maxX) maxX = x;
                    if (y > maxY) maxY = y;
                }
            }
        }

        if (maxX < minX || maxY < minY) {
            return canvas;
        }

        const trimmedWidth = maxX - minX + 1;
        const trimmedHeight = maxY - minY + 1;

        const trimmedCanvas = document.createElement('canvas');
        trimmedCanvas.width = trimmedWidth;
        trimmedCanvas.height = trimmedHeight;
        const trimmedCtx = trimmedCanvas.getContext('2d');
        trimmedCtx.drawImage(canvas, minX, minY, trimmedWidth, trimmedHeight, 0, 0, trimmedWidth, trimmedHeight);

        return trimmedCanvas;
    } catch {
        return canvas;
    }
};

const getLogoBase64 = (src) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);

            // Trim transparent padding to get accurate dimensions and prevent squeezing
            const trimmedCanvas = trimCanvas(canvas);

            resolve({
                base64: trimmedCanvas.toDataURL('image/png'),
                width: trimmedCanvas.width,
                height: trimmedCanvas.height
            });
        };
        img.onerror = () => {
            resolve(null);
        };
        img.src = src;
    });
};

// ── Page Decorations Header & Footer ───────────────────────────────────────────
const drawPageDecorations = (pdf, pageNum, totalPages, logoData) => {
    // 1. Draw Header top line (x margin: 15mm to 195mm, y = 16mm)
    pdf.setDrawColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.setLineWidth(0.4);
    pdf.line(15, 16, 195, 16);

    // Accent line right under the header in Brand Orange
    pdf.setDrawColor(34, 118, 233); // Brand Blue: #2276E9
    pdf.setLineWidth(1.0);
    pdf.line(15, 16.6, 35, 16.6); // 20mm Accent block

    // Draw Logo with dynamic aspect ratio preservation
    if (logoData && logoData.base64) {
        try {
            // Keep height at 8mm, scale width proportionally to prevent squeezing
            const logoH = 8;
            const logoW = (logoData.width * logoH) / logoData.height;
            pdf.addImage(logoData.base64, 'PNG', 15, 5, logoW, logoH);
        } catch {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            pdf.setTextColor(34, 118, 233); // Brand Blue
            pdf.text('EXALOGIC', 15, 12);
        }
    } else {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(34, 118, 233); // Brand Blue
        pdf.text('EXALOGIC', 15, 12);
    }

    // Header Right
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.text('VELOCITY RFP — EXECUTIVE REPORT', 195, 11, { align: 'right' });

    // 2. Draw Footer bottom line (x margin: 15mm to 195mm, y = 280mm)
    pdf.setDrawColor(74, 79, 83);
    pdf.setLineWidth(0.3);
    pdf.line(15, 280, 195, 280);

    // Left Footer
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(100, 116, 139);
    pdf.text('CONFIDENTIAL — FOR INTERNAL USE ONLY', 15, 286);

    // Center Footer
    const dateStr = new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    pdf.text(`Generated on: ${dateStr}`, 105, 286, { align: 'center' });

    // Right Footer
    pdf.text(`Page ${pageNum} of ${totalPages}`, 195, 286, { align: 'right' });
};

// ── Page 1: Overview, Compact KPI Table, Stacked Charts ───────────────────────
const drawPage1Overview = (pdf, data, logoData, chartCanvases) => {
    // Left brand sidebar highlight line in Brand Blue: #2276E9
    pdf.setFillColor(34, 118, 233);
    pdf.rect(15, 23, 2, 16, 'F');

    // Report Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(14);
    pdf.setTextColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.text('EXECUTIVE DASHBOARD REPORT', 22, 28);

    // Subtitle
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(100, 116, 139); // #64748B
    pdf.text('EDGE RFP Performance Analytics & Resource Insights Overview', 22, 33);

    // Metadata Filter Period
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(71, 85, 105); // #475569
    const filterText = data.preset === 'custom'
        ? `REPORTING PERIOD: ${data.fromDate || 'TBD'} TO ${data.toDate || 'TBD'}`
        : `REPORTING PERIOD: ${String(data.preset || 'ALL').replace('_', ' ').toUpperCase()}`;
    pdf.text(filterText, 22, 38);

    // --- Compact KPI Table with Brand Theme ---
    const kpiY = 43;
    pdf.setFillColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.rect(15, kpiY, 180, 7, 'F');

    // KPI Headers
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(255, 255, 255); // White: #FFFFFF
    pdf.text('ACTIVE RFPs', 15 + 22.5, kpiY + 5, { align: 'center' });
    pdf.text('WIN RATE', 60 + 22.5, kpiY + 5, { align: 'center' });
    pdf.text('TOTAL PIPELINE VALUE', 105 + 22.5, kpiY + 5, { align: 'center' });
    pdf.text('TOTAL ACHIEVED VALUE', 150 + 22.5, kpiY + 5, { align: 'center' });

    // KPI Values
    pdf.setFillColor(255, 255, 255); // White: #FFFFFF
    pdf.rect(15, kpiY + 7, 180, 10, 'F');
    pdf.setDrawColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.rect(15, kpiY + 7, 180, 10, 'D');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(10);
    pdf.setTextColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.text(String(data.activeRFPs ?? 0), 15 + 22.5, kpiY + 14, { align: 'center' });
    pdf.text(`${data.winRate ?? 0}%`, 60 + 22.5, kpiY + 14, { align: 'center' });
    pdf.text(data.pipelineValDisplay || 'AED 0', 105 + 22.5, kpiY + 14, { align: 'center' });
    pdf.text(data.achievedValDisplay || 'AED 0', 150 + 22.5, kpiY + 14, { align: 'center' });

    // --- Analytics Charts (Side-by-Side Compact Card Layout) ---
    const chartW = 87;
    const chartH = 75;
    const chartsY = 66;

    // Chart 1: Stage Distribution
    const distX = 15;
    pdf.setFillColor(255, 255, 255); // White
    pdf.rect(distX, chartsY, chartW, chartH, 'F');
    pdf.setDrawColor(74, 79, 83); // Primary Gray
    pdf.rect(distX, chartsY, chartW, chartH, 'D');

    // Mini brand accent bar at the top of the card
    pdf.setFillColor(34, 118, 233); // Brand Blue
    pdf.rect(distX, chartsY, chartW, 1.5, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.setTextColor(74, 79, 83); // Primary Gray
    pdf.text('STAGE DISTRIBUTION', distX + 4, chartsY + 6);

    pdf.setDrawColor(226, 232, 240);
    pdf.line(distX, chartsY + 9, distX + chartW, chartsY + 9);

    if (chartCanvases.stageDist) {
        try {
            pdf.addImage(chartCanvases.stageDist, 'PNG', distX + 3, chartsY + 12, chartW - 6, chartH - 16);
        } catch (err) {
            console.error('Failed drawing stage distribution chart:', err);
        }
    }

    // Chart 2: Win Rate Trends
    const trendX = 108;
    pdf.setFillColor(255, 255, 255); // White
    pdf.rect(trendX, chartsY, chartW, chartH, 'F');
    pdf.setDrawColor(74, 79, 83); // Primary Gray
    pdf.rect(trendX, chartsY, chartW, chartH, 'D');

    // Mini brand accent bar at the top of the card
    pdf.setFillColor(34, 118, 233); // Brand Blue
    pdf.rect(trendX, chartsY, chartW, 1.5, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(8.5);
    pdf.setTextColor(74, 79, 83); // Primary Gray
    pdf.text('WIN RATE TRENDS', trendX + 4, chartsY + 6);

    pdf.setDrawColor(226, 232, 240);
    pdf.line(trendX, chartsY + 9, trendX + chartW, chartsY + 9);

    if (chartCanvases.winTrends) {
        try {
            pdf.addImage(chartCanvases.winTrends, 'PNG', trendX + 3, chartsY + 12, chartW - 6, chartH - 16);
        } catch (err) {
            console.error('Failed drawing win rate trends chart:', err);
        }
    }
};

// ── Table Drawers ─────────────────────────────────────────────────────────────
const drawTeamAllocation = (pdf, data, startY) => {
    // Left brand sidebar highlight line in Brand Blue: #2276E9
    pdf.setFillColor(34, 118, 233);
    pdf.rect(15, startY, 1.5, 9.5, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.text('RESOURCE MANAGEMENT & TEAM WORKLOAD', 19, startY + 6);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text('Active section workloads and capacity utilization status', 19, startY + 10);

    const team = data.teamWorkload || [];
    const teamStartY = startY + 14;
    pdf.setFillColor(74, 79, 83); // Theme Primary Gray: #4A4F53
    pdf.rect(15, teamStartY, 180, 8, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(255, 255, 255); // White
    pdf.text('RESOURCE NAME', 18, teamStartY + 5.5);
    pdf.text('ROLE', 53, teamStartY + 5.5);
    pdf.text('ACTIVE SECTIONS', 88, teamStartY + 5.5);
    pdf.text('UTILIZATION STATUS', 123, teamStartY + 5.5);
    pdf.text('DRIVING PROPOSALS', 153, teamStartY + 5.5);

    let rowY = teamStartY + 8;
    if (team.length === 0) {
        pdf.setFillColor(255, 255, 255); // White
        pdf.rect(15, rowY, 180, 20, 'F');
        pdf.setDrawColor(74, 79, 83);
        pdf.rect(15, rowY, 180, 20, 'D');

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(148, 163, 184);
        pdf.text('No resource allocations configured for this period.', 68, rowY + 12);
        rowY += 20;
    } else {
        team.slice(0, 8).forEach((member, idx) => {
            if (idx % 2 === 1) {
                pdf.setFillColor(248, 250, 252);
                pdf.rect(15, rowY, 180, 8.5, 'F');
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(51, 65, 85);

            pdf.text(member.full_name || 'N/A', 18, rowY + 5.5);
            pdf.text(member.role || 'Contributor', 53, rowY + 5.5);
            pdf.text(`${member.active_sections_count || 0} sections`, 88, rowY + 5.5);

            const status = (member.utilization_status || 'Available').toUpperCase();
            pdf.setFont('helvetica', 'bold');
            if (status.includes('AVAILABLE')) {
                pdf.setTextColor(16, 185, 129); // green
            } else if (status.includes('BALANCED')) {
                pdf.setTextColor(240, 74, 35); // Brand Orange
            } else if (status.includes('OVERLOADED')) {
                pdf.setTextColor(220, 38, 38); // red
            } else {
                pdf.setTextColor(74, 79, 83);
            }
            pdf.text(status, 123, rowY + 5.5);

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(74, 79, 83);
            const props = (member.driving_rfp_titles || []).join(', ');
            const propStr = props.length > 26 ? `${props.substring(0, 24)}...` : (props || 'None');
            pdf.text(propStr, 153, rowY + 5.5);

            rowY += 8.5;
            pdf.setDrawColor(241, 245, 249);
            pdf.line(15, rowY, 195, rowY);
        });
    }
};

const drawProposalsRegistry = (pdf, data, startY) => {
    // Left brand sidebar highlight line in Brand Blue: #2276E9
    pdf.setFillColor(34, 118, 233);
    pdf.rect(15, startY, 1.5, 9.5, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(74, 79, 83); // Theme Primary Gray
    pdf.text('ACTIVE PROPOSALS REGISTRY DIRECTORY', 19, startY + 6);

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(100, 116, 139);
    pdf.text('Current active proposals, pipeline stages, and target dates', 19, startY + 10);

    const list = data.rfpList || [];
    const propStartY = startY + 14;
    pdf.setFillColor(74, 79, 83); // Theme Primary Gray
    pdf.rect(15, propStartY, 180, 8, 'F');

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(7.5);
    pdf.setTextColor(255, 255, 255); // White
    pdf.text('PROPOSAL TITLE', 18, propStartY + 5.5);
    pdf.text('CLIENT NAME', 73, propStartY + 5.5);
    pdf.text('CURRENT STAGE / STATUS', 123, propStartY + 5.5);
    pdf.text('TARGET DATE', 163, propStartY + 5.5);

    let rowY = propStartY + 8;
    if (list.length === 0) {
        pdf.setFillColor(255, 255, 255);
        pdf.rect(15, rowY, 180, 20, 'F');
        pdf.setDrawColor(74, 79, 83);
        pdf.rect(15, rowY, 180, 20, 'D');

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(148, 163, 184);
        pdf.text('No active proposals mapped for this dashboard overview.', 68, rowY + 12);
    } else {
        list.slice(0, 24).forEach((p, idx) => {
            if (idx % 2 === 1) {
                pdf.setFillColor(248, 250, 252);
                pdf.rect(15, rowY, 180, 8.5, 'F');
            }

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.setTextColor(51, 65, 85);

            const title = String(p.proposal_title || p.proposal_name || p.extraction_data?.proposal_title || 'Untitled');
            const finalTitle = title.length > 28 ? `${title.substring(0, 26)}...` : title;
            pdf.text(finalTitle.toUpperCase(), 18, rowY + 5.5);

            const client = String(p.client_name || '—').substring(0, 24);
            pdf.text(client, 73, rowY + 5.5);

            pdf.setFont('helvetica', 'bold');
            const stageText = (p.status || p.proposal_stage || 'Drafting').toUpperCase();
            pdf.setTextColor(74, 79, 83);
            pdf.text(stageText, 123, rowY + 5.5);

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(71, 85, 105);
            pdf.text(p.proposal_deadline || p.deadline_display || 'TBD', 163, rowY + 5.5);

            rowY += 8.5;
            pdf.setDrawColor(241, 245, 249);
            pdf.line(15, rowY, 195, rowY);
        });

        if (list.length > 24) {
            pdf.setFont('helvetica', 'italic');
            pdf.setFontSize(7);
            pdf.setTextColor(148, 163, 184);
            pdf.text(`* Showing top 24 active proposals of ${list.length} registry items. View all in the dynamic RFP Board.`, 18, rowY + 6);
        }
    }
};

// ── Master Export PDF Function ──────────────────────────────────────────────────
/**
 * exportDashboardPDF()
 *
 * @param {object} options
 * @param {object} options.data        - Complete dashboard state/metrics payload
 * @param {function} options.onStart   - Start callback (disable UI, load loader)
 * @param {function} options.onSuccess - Success callback
 * @param {function} options.onError   - Error callback
 * @param {function} options.onFinish  - Final cleanup callback
 */
export async function exportDashboardPDF({ data, onStart, onSuccess, onError, onFinish } = {}) {
    if (!data) {
        onError?.('No dashboard metrics loaded to generate PDF report.');
        onFinish?.();
        return;
    }

    try {
        onStart?.();

        // 1. Fetch static ExalogicLogo image (and verify original aspect ratio dimensions)
        const logoData = await getLogoBase64(logoImg);

        // 2. High-DPI Capture of the two analytics charts individually
        const stageDistEl = document.querySelector('.db-charts-row > div:nth-child(1)');
        const winTrendsEl = document.querySelector('.db-charts-row > div:nth-child(2)');

        const chartCanvases = { stageDist: null, winTrends: null };
        let stageOriginals = [];
        let winOriginals = [];

        // Capture Stage Distribution Chart
        if (stageDistEl) {
            stageOriginals = inlineSVGs(stageDistEl);
            await new Promise((r) => setTimeout(r, 60));
            const canvas1 = await html2canvas(stageDistEl, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });
            chartCanvases.stageDist = canvas1.toDataURL('image/png');
            restoreSVGs(stageOriginals);
        }

        // Capture Win Rate Trends Chart
        if (winTrendsEl) {
            winOriginals = inlineSVGs(winTrendsEl);
            await new Promise((r) => setTimeout(r, 60));
            const canvas2 = await html2canvas(winTrendsEl, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                logging: false
            });
            chartCanvases.winTrends = canvas2.toDataURL('image/png');
            restoreSVGs(winOriginals);
        }

        // 3. Initialize Portrait A4 jsPDF instance
        const pdf = new jsPDF({
            orientation: PDF_ORIENTATION,
            unit: PDF_UNIT,
            format: PDF_FORMAT,
        });

        const totalPages = 2; // Reduced to 2 pages for optimal high-density space utilization

        // --- PAGE 1: Overview, KPIs, Charts & Team Allocation ---
        drawPage1Overview(pdf, data, logoData, chartCanvases);
        drawTeamAllocation(pdf, data, 152); // Starts dynamically below charts
        drawPageDecorations(pdf, 1, totalPages, logoData);

        // --- PAGE 2: Active proposals directory ---
        pdf.addPage();
        drawProposalsRegistry(pdf, data, 25);
        drawPageDecorations(pdf, 2, totalPages, logoData);

        // 4. Save and Download
        const dateStr = new Date().toISOString().split('T')[0];
        pdf.save(`Velocity_RFP_Dashboard_${dateStr}.pdf`);

        onSuccess?.();
    } catch (err) {
        console.error('[Dashboard Compact PDF Export] Error:', err);
        onError?.('PDF report generation failed. Please try again.');
    } finally {
        onFinish?.();
    }
}
